/**
 * ThermaSun Banner — stage background colour animation hook + pause-on-hover.
 *
 * Each <slideshow-slide> carries a data-stage-bg attribute (set from the
 * block's stage_background_color setting). When the active slide changes
 * we read that attribute from the new active slide and write it as a
 * --stage-bg CSS custom property on the .thermasun-banner-stage wrapper.
 * The CSS transition on background-color animates the change.
 *
 * Built on Horizon's <slideshow-component>, which dispatches a bubbling
 * 'slideshow:select' event (see assets/events.js: SlideshowSelectEvent)
 * with detail.index and detail.slide on every slide change — whether
 * triggered by arrow click, dot click, swipe, scroll, or keyboard.
 *
 * The stage element carries data-pause-on-hover; when "true" we wire
 * mouseenter/leave on the slideshow-component to call its pause/play
 * methods. The slideshow-component handles autoplay internally, so calling
 * play() when autoplay isn't set is a no-op (safe to wire unconditionally
 * — but we still respect the merchant toggle in case they want to opt out).
 */

import { SlideshowSelectEvent } from '@theme/events';

class ThermasunBannerStage {
  constructor(stage) {
    this.stage = stage;
    this.slideshow = stage.querySelector('slideshow-component');
    if (!this.slideshow) return;

    this.slideshow.addEventListener(SlideshowSelectEvent.eventName, this.handleSelect);
    this.applyStageBgFromSlide(this.getCurrentSlide());

    if (stage.dataset.pauseOnHover === 'true') {
      this.slideshow.addEventListener('mouseenter', this.handlePause);
      this.slideshow.addEventListener('mouseleave', this.handleResume);
      this.slideshow.addEventListener('focusin', this.handlePause);
      this.slideshow.addEventListener('focusout', this.handleResume);
    }
  }

  handleSelect = (event) => {
    const slide = event.detail?.slide ?? this.getCurrentSlide();
    this.applyStageBgFromSlide(slide);
  };

  handlePause = () => {
    this.slideshow.pause?.();
  };

  handleResume = () => {
    this.slideshow.play?.();
  };

  getCurrentSlide() {
    return this.slideshow.querySelector('slideshow-slide[aria-hidden="false"]')
      ?? this.slideshow.querySelector('slideshow-slide');
  }

  applyStageBgFromSlide(slide) {
    if (!slide) return;
    const bg = slide.getAttribute('data-stage-bg');
    if (bg) this.stage.style.setProperty('--stage-bg', bg);
  }
}

const init = () => {
  document.querySelectorAll('.thermasun-banner-stage').forEach((stage) => {
    if (stage.dataset.thermasunBannerInit === 'true') return;
    stage.dataset.thermasunBannerInit = 'true';
    new ThermasunBannerStage(stage);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
